/**
 * useDealAttachments - Hook para gerenciar anexos de negócios CRM
 * Suporte a upload, listagem e exclusão de arquivos
 * 
 * Versão: 1.0.0
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

// Tipos de arquivo suportados
export type AttachmentFileType = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other';

// Interface do anexo
export interface DealAttachment {
  id: string;
  tenant_id: string;
  deal_id: string;
  file_name: string;
  file_type: AttachmentFileType;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  metadata: Record<string, unknown>;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

// Limites de upload
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ATTACHMENTS = 20;

// Tipos MIME permitidos
const ALLOWED_MIME_TYPES: Record<string, AttachmentFileType> = {
  // Imagens
  'image/jpeg': 'image',
  'image/jpg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  // PDFs
  'application/pdf': 'pdf',
  // Documentos
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'text/plain': 'document',
  // Planilhas
  'application/vnd.ms-excel': 'spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
};

/**
 * Determina o tipo do arquivo baseado no MIME type
 */
function getFileType(mimeType: string): AttachmentFileType {
  return ALLOWED_MIME_TYPES[mimeType] || 'other';
}

/**
 * Valida se o arquivo é permitido
 */
function validateFile(file: File): { valid: boolean; error?: string } {
  // Verificar tamanho
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Arquivo muito grande. Máximo: 10MB' };
  }
  
  // Verificar tipo
  if (!ALLOWED_MIME_TYPES[file.type]) {
    return { valid: false, error: 'Tipo de arquivo não permitido' };
  }
  
  return { valid: true };
}

/**
 * Sanitiza o nome do arquivo para evitar problemas de segurança
 * Remove acentos, espaços e caracteres especiais
 */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/**
 * Opções do hook useDealAttachments
 */
export interface UseDealAttachmentsOptions {
  /** Habilita a listagem de anexos (default true). Use false em telas que só fazem upload/delete. */
  enabled?: boolean;
}

/**
 * Hook principal para gerenciar anexos de negócios
 */
export function useDealAttachments(
  dealId: string | undefined,
  options?: UseDealAttachmentsOptions,
) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Listagem habilitada por padrão; consumidores que só usam mutations
  // podem passar { enabled: false } para evitar requisições desnecessárias
  const listEnabled = options?.enabled ?? true;

  // Query para buscar anexos do deal via RPC server-side (get_deal_attachments)
  // Substitui o SELECT direto na tabela crm_deal_attachments para respeitar
  // o padrão de segurança SECURITY DEFINER + validação de tenant/fase
  const attachmentsQuery = useQuery({
    queryKey: ['deal-attachments', dealId, currentTenant?.id],
    queryFn: async () => {
      if (!dealId) return [];

      const { data, error } = await supabase.rpc('get_deal_attachments' as never, {
        p_deal_id: dealId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);

      if (error) {
        logger.error('Erro ao buscar anexos:', error.message);
        throw error;
      }

      return (data ?? []) as DealAttachment[];
    },
    enabled: !!dealId && !!currentTenant?.id && listEnabled,
  });

  // Mutation para upload de arquivo
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!dealId || !currentTenant?.id) {
        throw new Error('Deal ou tenant não definido');
      }

      // Validar arquivo
      const validation = validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Verificar limite de anexos
      const currentCount = attachmentsQuery.data?.length || 0;
      if (currentCount >= MAX_ATTACHMENTS) {
        throw new Error(`Limite de ${MAX_ATTACHMENTS} anexos atingido`);
      }

      // Preparar dados do arquivo
      const fileType = getFileType(file.type);
      const sanitizedName = sanitizeFileName(file.name);
      const timestamp = Date.now();
      // Path padronizado: prefixo "Deals/" para evitar pastas UUID na raiz do bucket
      const storagePath = `Deals/${currentTenant.id}/${dealId}/${timestamp}_${sanitizedName}`;

      // Upload para o bucket "anexos"
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        logger.error('Erro no upload:', uploadError.message);
        throw new Error('Falha no upload do arquivo');
      }

      // Obter URL pública
      const { data: urlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      // Criar registro no banco
      const { data: attachment, error: insertError } = await supabase
        .from('crm_deal_attachments')
        .insert({
          tenant_id: currentTenant.id,
          deal_id: dealId,
          file_name: sanitizedName,
          file_type: fileType,
          mime_type: file.type,
          file_size: file.size,
          storage_path: storagePath,
          public_url: urlData.publicUrl,
          thumbnail_url: fileType === 'image' ? urlData.publicUrl : null,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (insertError) {
        // Tentar limpar o arquivo do storage se falhou o insert
        await supabase.storage.from('anexos').remove([storagePath]);
        logger.error('Erro ao registrar anexo:', insertError.message);
        throw new Error('Falha ao registrar anexo');
      }

      return attachment as DealAttachment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-attachments', dealId] });
      toast({
        title: 'Arquivo enviado',
        description: 'Anexo adicionado com sucesso',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro no upload',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para deletar anexo
  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      if (!dealId) throw new Error('Deal não definido');

      // Buscar dados do anexo
      const { data: attachment, error: fetchError } = await supabase
        .from('crm_deal_attachments')
        .select('storage_path')
        .eq('id', attachmentId)
        .single();

      if (fetchError || !attachment) {
        throw new Error('Anexo não encontrado');
      }

      // Deletar do storage
      const { error: storageError } = await supabase.storage
        .from('anexos')
        .remove([attachment.storage_path]);

      if (storageError) {
        logger.warn('Erro ao deletar do storage:', storageError.message);
        // Continuar mesmo se falhar no storage
      }

      // Deletar registro
      const { error: deleteError } = await supabase
        .from('crm_deal_attachments')
        .delete()
        .eq('id', attachmentId);

      if (deleteError) {
        throw new Error('Falha ao deletar anexo');
      }

      return attachmentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-attachments', dealId] });
      toast({
        title: 'Anexo removido',
        description: 'Arquivo excluído com sucesso',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    // Dados
    attachments: attachmentsQuery.data || [],
    isLoading: attachmentsQuery.isLoading,
    error: attachmentsQuery.error,
    
    // Mutations
    uploadFile: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutate,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Utilidades
    canAddMore: (attachmentsQuery.data?.length || 0) < MAX_ATTACHMENTS,
    maxAttachments: MAX_ATTACHMENTS,
    maxFileSize: MAX_FILE_SIZE,
  };
}
